import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import api from '../../utils/api';
import { Plus, Trash2, Search, GripVertical, X, Filter } from 'lucide-react';
import { toast } from 'sonner';

const RouteManager = () => {
  const [routes, setRoutes] = useState([]);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    destination_group: '',
    place_id: '',
    min_nights: '',
    max_nights: '',
  });
  
  const [formData, setFormData] = useState({
    route_code: '',
    route_name: '',
    destination_group: '',
    route_type: 'PREDEFINED',
    arrival_city: '',
    departure_city: '',
    stops: [],
  });

  useEffect(() => {
    fetchRoutes();
    fetchPlaces();
  }, []);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      let url = '/routes';
      const params = new URLSearchParams();
      if (filters.destination_group) params.append('destination_group', filters.destination_group);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      let filteredRoutes = response.data;
      
      // Client-side filtering
      if (filters.place_id) {
        filteredRoutes = filteredRoutes.filter(r => 
          r.stops?.some(s => s.place_id === filters.place_id)
        );
      }
      if (filters.min_nights) {
        filteredRoutes = filteredRoutes.filter(r => r.total_nights >= parseInt(filters.min_nights));
      }
      if (filters.max_nights) {
        filteredRoutes = filteredRoutes.filter(r => r.total_nights <= parseInt(filters.max_nights));
      }
      
      setRoutes(filteredRoutes);
    } catch (error) {
      toast.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaces = async () => {
    try {
      const response = await api.get('/places');
      setPlaces(response.data);
    } catch (error) {
      toast.error('Failed to load places');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation
    if (!formData.route_name?.trim()) {
      toast.error('Please enter a Route Name');
      return;
    }
    if (!formData.arrival_city) {
      toast.error('Please select an Arrival City');
      return;
    }
    if (!formData.departure_city) {
      toast.error('Please select a Departure City');
      return;
    }
    if (!formData.destination_group) {
      toast.error('Please select a Destination Group');
      return;
    }
    if (formData.stops.length === 0) {
      toast.error('Please add at least one stop');
      return;
    }
    
    // Validate all stops have place_id selected
    const invalidStop = formData.stops.find(stop => !stop.place_id);
    if (invalidStop) {
      toast.error('Please select a place for all stops');
      return;
    }

    try {
      const data = {
        ...formData,
        stops: formData.stops.map((stop, idx) => ({
          ...stop,
          order_seq: idx + 1,
        })),
      };
      
      console.log('Submitting route data:', data); // Debug log
      
      await api.post('/routes', data);
      toast.success('Route created successfully');
      setDialogOpen(false);
      resetForm();
      fetchRoutes();
    } catch (error) {
      console.error('Route creation error:', error.response?.data); // Debug log
      const errorDetail = error.response?.data?.detail;
      if (errorDetail) {
        toast.error(errorDetail);
      } else if (error.response?.status === 422) {
        toast.error('Validation error: Please check all required fields');
      } else {
        toast.error('Failed to save route. Please try again.');
      }
    }
  };

  const handleDelete = async (routeId) => {
    if (!window.confirm('Are you sure you want to delete this route?')) return;
    try {
      await api.delete(`/routes/${routeId}`);
      toast.success('Route deleted successfully');
      fetchRoutes();
    } catch (error) {
      toast.error('Failed to delete route');
    }
  };

  const resetForm = () => {
    setFormData({
      route_code: '',
      route_name: '',
      destination_group: '',
      route_type: 'PREDEFINED',
      arrival_city: '',
      departure_city: '',
      stops: [],
    });
  };

  // Auto-generate route code based on destination group
  const generateRouteCode = (destGroup) => {
    if (!destGroup) return '';
    
    // Get prefix from destination group (first 2 letters, uppercase)
    const prefix = destGroup.substring(0, 2).toUpperCase();
    
    // Find existing routes with same prefix
    const existingCodes = routes
      .filter(r => r.route_code?.startsWith(prefix))
      .map(r => {
        const numPart = r.route_code?.replace(prefix, '');
        return parseInt(numPart) || 0;
      });
    
    // Get next number
    const nextNum = existingCodes.length > 0 ? Math.max(...existingCodes) + 1 : 1;
    
    return `${prefix}${nextNum}`;
  };

  // Update route code when destination group changes
  const handleDestinationGroupChange = (value) => {
    const newCode = generateRouteCode(value);
    setFormData({ 
      ...formData, 
      destination_group: value,
      route_code: newCode
    });
  };

  const addStop = () => {
    setFormData({
      ...formData,
      stops: [
        ...formData.stops,
        { place_id: '', place_name: '', nights: 1, order_seq: formData.stops.length + 1 },
      ],
    });
  };

  const removeStop = (index) => {
    const newStops = formData.stops.filter((_, i) => i !== index);
    setFormData({ ...formData, stops: newStops });
  };

  const updateStop = (index, field, value) => {
    const newStops = [...formData.stops];
    newStops[index] = { ...newStops[index], [field]: value };
    
    if (field === 'place_id') {
      const place = places.find(p => p.place_id === value);
      newStops[index].place_name = place?.place_name || '';
    }
    
    setFormData({ ...formData, stops: newStops });
  };

  const getTotalNights = () => {
    return formData.stops.reduce((sum, stop) => sum + (parseInt(stop.nights) || 0), 0);
  };

  const applyFilters = () => {
    fetchRoutes();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ destination_group: '', place_id: '', min_nights: '', max_nights: '' });
    fetchRoutes();
  };

  const filteredRoutes = routes.filter(
    (r) =>
      r.route_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.route_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.destination_group.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get unique destination groups
  const destinationGroups = [...new Set(routes.map(r => r.destination_group))].filter(Boolean).sort();

  return (
    <Layout>
      <div className="space-y-6" data-testid="route-manager-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Route Manager</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">{filteredRoutes.length} routes</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm} data-testid="add-route-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Route
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Route</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Route Code <span className="text-xs text-gray-500">(Auto-generated)</span></Label>
                      <Input
                        value={formData.route_code}
                        placeholder="Will be auto-generated"
                        data-testid="route-code-input"
                        required
                        readOnly
                        className="bg-gray-100"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Destination Group *</Label>
                      <Input
                        value={formData.destination_group}
                        onChange={(e) => handleDestinationGroupChange(e.target.value)}
                        placeholder="e.g., Kerala"
                        data-testid="route-destination-group-input"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Route Name *</Label>
                    <Input
                      value={formData.route_name}
                      onChange={(e) => setFormData({ ...formData, route_name: e.target.value })}
                      placeholder="e.g., Kerala Classic"
                      data-testid="route-name-input"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Arrival City *</Label>
                      <SearchableSelect
                        options={places.map(p => ({ value: p.place_name, label: p.place_name }))}
                        value={formData.arrival_city || ""}
                        onValueChange={(v) => setFormData({ ...formData, arrival_city: v })}
                        placeholder="Select arrival city"
                        searchPlaceholder="Search cities..."
                        data-testid="arrival-city-select"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Departure City *</Label>
                      <SearchableSelect
                        options={places.map(p => ({ value: p.place_name, label: p.place_name }))}
                        value={formData.departure_city || ""}
                        onValueChange={(v) => setFormData({ ...formData, departure_city: v })}
                        placeholder="Select departure city"
                        searchPlaceholder="Search cities..."
                        data-testid="departure-city-select"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Route Type</Label>
                    <Select
                      value={formData.route_type}
                      onValueChange={(v) => setFormData({ ...formData, route_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PREDEFINED">Predefined</SelectItem>
                        <SelectItem value="CUSTOM">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label>Stops ({getTotalNights()} nights, {getTotalNights() + 1} days)</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addStop} data-testid="route-add-stop-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Stop
                      </Button>
                    </div>
                    
                    {formData.stops.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4 border rounded-lg border-dashed">
                        No stops added. Click "Add Stop" to begin.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {formData.stops.map((stop, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50"
                          >
                            <div className="cursor-move text-gray-400">
                              <GripVertical className="w-4 h-4" />
                            </div>
                            <span className="text-sm font-medium text-gray-500 w-6">{index + 1}.</span>
                            <SearchableSelect
                              options={places.map(p => ({ value: p.place_id, label: p.place_name }))}
                              value={stop.place_id || ""}
                              onValueChange={(v) => updateStop(index, 'place_id', v)}
                              placeholder="Select place"
                              searchPlaceholder="Search places..."
                              className="flex-1"
                              data-testid={`route-stop-place-${index}`}
                            />
                            <div className="flex items-center gap-1 w-24">
                              <Input
                                type="number"
                                min="1"
                                value={stop.nights}
                                onChange={(e) => updateStop(index, 'nights', parseInt(e.target.value) || 1)}
                                className="w-16"
                              />
                              <span className="text-sm text-gray-500">N</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeStop(index)}
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1" data-testid="route-submit-btn">
                      Create
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {showFilters && (
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label>Destination</Label>
                  <Select value={filters.destination_group || "all"} onValueChange={(v) => setFilters({ ...filters, destination_group: v === "all" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All destinations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All destinations</SelectItem>
                      {destinationGroups.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Includes Place</Label>
                  <Select value={filters.place_id || "all"} onValueChange={(v) => setFilters({ ...filters, place_id: v === "all" ? "" : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Any place" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any place</SelectItem>
                      {places.map((p) => (
                        <SelectItem key={p.place_id} value={p.place_id}>{p.place_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Min Nights</Label>
                  <Input
                    type="number"
                    min="1"
                    value={filters.min_nights}
                    onChange={(e) => setFilters({ ...filters, min_nights: e.target.value })}
                    placeholder="Min"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Nights</Label>
                  <Input
                    type="number"
                    min="1"
                    value={filters.max_nights}
                    onChange={(e) => setFilters({ ...filters, max_nights: e.target.value })}
                    placeholder="Max"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={applyFilters} className="flex-1">Apply</Button>
                  <Button variant="outline" onClick={clearFilters}>Clear</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="pt-6">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search routes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="route-search-input"
                />
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredRoutes.length === 0 ? (
              <div className="text-center py-8 text-gray-600">No routes found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Route Code</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Route Name</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Destination</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Stops</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Duration</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRoutes.map((route) => (
                      <tr key={route.route_id} className="border-b hover:bg-gray-50" data-testid={`route-row-${route.route_id}`}>
                        <td className="py-3 px-4 text-sm font-medium">{route.route_code}</td>
                        <td className="py-3 px-4 text-sm">
                          <div className="max-w-[200px] whitespace-normal break-words">
                            {route.route_name}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{route.destination_group}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <div className="max-w-[250px] whitespace-normal break-words text-xs leading-relaxed">
                            {route.stops?.map(s => `${s.place_name} (${s.nights}N)`).join(' → ')}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          <span className="font-medium">{route.total_nights}N / {route.total_days}D</span>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              route.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {route.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(route.route_id)} data-testid={`route-delete-btn-${route.route_id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default RouteManager;
